import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

// mui components
import { Stack, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from "@mui/material";

// custom components
import PrimaryButton from "../../buttons/primary-button";
import SecondaryButton from "../../buttons/secondary-button";
import Body3 from "../../typography/body-03";
import DirectPathStatus from "../../direct-path-status";
import TableHeadUploadHistory from "../../custom-table/table-head/table-head-upload-history";
import ProcessingModal from "../../modal/processing-modal";

// api slices
import { useUploadXmlHistoryMutation } from "../../../features/upload-xml/uploadXmlHistoryApiSlice";
import { useUpdatePendingApplicationDirectPathMutation } from "../../../features/direct-path/updatePendingApplicationDirectPathApiSlice";

// reducer slices
import {
	selectUserDetails,
	selectSelectedOrganization,
} from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// mui icons
import { formatInTimeZone } from 'date-fns-tz';
import { format } from "date-fns";

// sort utilities
import { sortDate, sortAmount } from "../../../utils/sort-methods";
import { isCommercialBrokerChannel } from "../../../utils/common-helpers";

// hooks
import useDebounce from "../../../hooks/useDebounce";
import { useOrganizationId } from "../../../features/auth/useOrganizationId";

// custom styles
import styles from "./index.module.scss";
import Body2 from "../../typography/body-02";

const UploadHistoryTable = ({
	pageSize = 25,
	maxHeight = 560,
	minHeight = 420,
	isFullPage = false,
	onRowClick,
	onCountChange,
	fixedHeader = false,
	showLoadingModal = true,
	onLoadingChange,
	fetchData = true,
	fetchCount = true,
}) => {
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	// DirectPath column is Commercial Broker only. Non-CB history stays unchanged.
	const showDirectPath = isCommercialBrokerChannel(
		userDetails,
		impersonatedUser,
		selectedOrganization
	);
	// Optimistic view of the row selection while the update is in flight.
	const [directPathByRow, setDirectPathByRow] = useState({});

	// Hook to ensure organization ID is set when organization changes
	useOrganizationId();

	const [uploadHistoryDataCall] = useUploadXmlHistoryMutation();
	const [updatePendingApplicationDirectPath] =
		useUpdatePendingApplicationDirectPathMutation();

	// Upload history states
	const [uploadHistory, setUploadHistory] = useState([]);
	const [uploadHistoryLoading, setUploadHistoryLoading] = useState(false);
	const [historyPage, setHistoryPage] = useState(1);
	const [historyTotal, setHistoryTotal] = useState(undefined); // undefined until loaded
	const [historyHasNext, setHistoryHasNext] = useState(false);

	// Store the true total from the API (no filters)
	const [apiTotal, setApiTotal] = useState(undefined); // undefined until loaded

	const [filters, setFilters] = useState({
		borrower_name: "",
		status: "",
		loan_application_date: null,
		loan_application_completed_date: null,
		application_number: "",
	});
	const debouncedFilters = useDebounce(filters, 500);


	// Filters that should go to API (exclude local-only filters like application_number)
	const apiFilters = useMemo(() => {
		const { application_number, ...rest } = debouncedFilters || {};
		return rest;
	}, [debouncedFilters]);


	// Call onCountChange whenever apiTotal changes (not historyTotal)
	useEffect(() => {
		if (typeof apiTotal === 'number') {
			onCountChange?.(apiTotal);
		}
	}, [apiTotal, onCountChange]);

	// Sorting states
	const [sortOrder, setSortOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState('');

	const uploadHistoryColumns = useMemo(() => {
		const columns = [
			{ id: "borrower_name", label: "Borrower Name", minWidth: 220 },
			{ id: "status", label: "Status", minWidth: 260 },
			{ id: "loan_application_date", label: "Loan XML Upload Date", minWidth: 260 },
			{ id: "loan_application_completed_date", label: "Loan Submission Date", minWidth: 260 },
		];

		if (showDirectPath) {
			columns.push({ id: "direct_path", label: "DirectPath", minWidth: 160 });
		}

		columns.push({ id: "action", label: "Go To Loan", minWidth: 180 });
		return columns;
	}, [showDirectPath]);

	// Pending rows share an empty application_number, so keying on it makes every
	// pending row resolve to the same key and toggle together.
	const getHistoryRowKey = (item, index) => {
		const rowId =
			item?.id ?? item?.loan_guid ?? item?.loanGuid ?? item?.application_number;

		return rowId === undefined || rowId === null || rowId === ""
			? `row-${index}`
			: `row-id-${rowId}`;
	};

	// A loan number is assigned when the loan is created, which is also when the
	// history row receives its loan guid. The selection is locked from that point.
	const isDirectPathLocked = (item) => Boolean(item?.loan_guid || item?.loanGuid);

	const getDirectPathSelected = (item, index) => {
		const rowKey = getHistoryRowKey(item, index);
		if (Object.prototype.hasOwnProperty.call(directPathByRow, rowKey)) {
			return Boolean(directPathByRow[rowKey]);
		}
		return Boolean(item?.direct_path ?? item?.directPath);
	};

	const handleDirectPathToggle = async (item, index, isDirectPath) => {
		if (isDirectPathLocked(item)) return;

		const rowKey = getHistoryRowKey(item, index);
		const previousSelection = getDirectPathSelected(item, index);

		setDirectPathByRow((prev) => ({
			...prev,
			[rowKey]: isDirectPath,
		}));

		try {
			await updatePendingApplicationDirectPath({
				loanHistoryId: item?.id,
				directPath: isDirectPath,
			}).unwrap();
		} catch (err) {
			console.log("handleDirectPathToggle err:", err);
			setDirectPathByRow((prev) => ({
				...prev,
				[rowKey]: previousSelection,
			}));
		}
	};

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && sortOrder === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		setSortOrder(newOrder);
		setOrderBy(property);

		let sortedData;
		if (property === 'borrower_name') {
			sortedData = sortAmount(uploadHistory, newOrder, 'borrower_name');
		} else if (property === 'status') {
			sortedData = sortAmount(uploadHistory, newOrder, 'status');
		} else if (property === 'loan_application_date') {
			sortedData = sortDate(uploadHistory, newOrder, 'loan_application_date');
		} else if (property === 'loan_application_completed_date') {
			sortedData = sortDate(uploadHistory, newOrder, 'loan_application_completed_date');
		} else if (property === 'action') {
			sortedData = sortAmount(uploadHistory, newOrder, 'application_number');
		}
		setUploadHistory(sortedData || uploadHistory);
	};

	const buildFiltersForApi = (activeFilters) => {
		const params = {};
		if (activeFilters?.borrower_name) {
			const name = activeFilters.borrower_name.trim();
			const capitalized = name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
			params.borrower_name = capitalized || name;
		}
		if (activeFilters?.status) params.status = activeFilters.status.trim();
		if (activeFilters?.loan_application_date) params.loan_application_date = format(activeFilters.loan_application_date, "yyyy-MM-dd");
		if (activeFilters?.loan_application_completed_date) params.loan_application_completed_date = format(activeFilters.loan_application_completed_date, "yyyy-MM-dd");
		return params;
	};


	// Always fetch the unfiltered count from the API (for the counter)
	const fetchApiTotal = async () => {
		const tpoUserId = userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id;
		if (!tpoUserId) return;
		try {
			const fileUploadHistory = await uploadHistoryDataCall({
				tpo_user_id: tpoUserId,
				page: 1,
				pageSize,
				filters: {}, // no filters
				userDetails: userDetails,
				selectedOrganization: selectedOrganization,
			}).unwrap();
			if (Array.isArray(fileUploadHistory?.results)) {
				if (typeof fileUploadHistory?.count === 'number') setApiTotal(fileUploadHistory.count);
			} else if (Array.isArray(fileUploadHistory?.response)) {
				setApiTotal(fileUploadHistory.response.length);
			} else if (Array.isArray(fileUploadHistory)) {
				setApiTotal(fileUploadHistory.length);
			}
			// Do NOT setApiTotal(0) on loading or error; keep previous value until new one arrives
		} catch (err) {
			// ignore, keep previous apiTotal
		}
	};

	// Fetch the filtered data for the table, but do not update the counter
	const handleLoadData = async (page = 1, activeFilters = apiFilters) => {
		setUploadHistoryLoading(true);
		onLoadingChange?.(true);
		setHistoryHasNext(false);
		const tpoUserId = userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id;
		if (!tpoUserId) {
			setUploadHistoryLoading(false);
			onLoadingChange?.(false);
			throw new Error("tpo_user_id not available for upload history");
		}
		try {
			const filtersForApi = buildFiltersForApi(activeFilters);
			const fileUploadHistory = await uploadHistoryDataCall({
				tpo_user_id: tpoUserId,
				page,
				pageSize,
				filters: filtersForApi,
				userDetails: userDetails,
				selectedOrganization: selectedOrganization,
			}).unwrap();
			let rows = [];
			if (Array.isArray(fileUploadHistory?.response)) {
				rows = fileUploadHistory.response;
				setHistoryTotal(fileUploadHistory.response.length);
				setHistoryHasNext(false);
			} else if (Array.isArray(fileUploadHistory?.results)) {
				rows = fileUploadHistory.results;
				setHistoryTotal(
					typeof fileUploadHistory?.count === 'number' ? fileUploadHistory.count : undefined
				);
				setHistoryHasNext(Boolean(fileUploadHistory?.next));
			} else if (Array.isArray(fileUploadHistory)) {
				rows = fileUploadHistory;
				setHistoryTotal(fileUploadHistory.length);
				setHistoryHasNext(false);
			}
			setUploadHistory(rows || []);
			setHistoryPage(page);
		} catch (err) {
			console.log("handleUploadHistory err:", err);
		} finally {
			setUploadHistoryLoading(false);
			onLoadingChange?.(false);
		}
	};

	const handleUploadHistoryRowClick = (item, index) => {
		if (!onRowClick) return;

		// Carry the current DirectPath toggle into the resume flow so create_loan
		// receives the broker's choice. Non-CB rows stay unchanged.
		if (showDirectPath) {
			const directPathSelected = getDirectPathSelected(item, index);
			onRowClick({
				...item,
				direct_path: directPathSelected,
				directPath: directPathSelected,
			});
			return;
		}

		onRowClick(item);
	};

	const handleFiltersChange = (name, value) => {
		setHistoryPage(1);
		setFilters((prev) => ({
			...prev,
			[name]: value ?? (name.includes("date") ? null : ""),
		}));
	};

	// Local-only filter for Action column (application_number)
	const filteredRows = useMemo(() => {
		const base = uploadHistory || [];
		const search = (filters?.application_number || "").toString().toLowerCase().trim();
		if (!search) return base;
		return base.filter((item) =>
			(item?.application_number || "")
				.toString()
				.toLowerCase()
				.includes(search)
		);
	}, [uploadHistory, filters?.application_number]);


	// Prevent duplicate calls on mount/user change/filter change
	const lastQueryRef = useRef({ userId: null, filtersKey: null, page: 1, organization: null });
	useEffect(() => {
		if (!fetchData && !fetchCount) return;
		const tpoUserId = userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id;
		if (!tpoUserId) return;

		const filtersKey = JSON.stringify(apiFilters || {});
		const isSameQuery =
			lastQueryRef.current.userId === tpoUserId &&
			lastQueryRef.current.filtersKey === filtersKey &&
			lastQueryRef.current.page === 1 &&
			lastQueryRef.current.organization === selectedOrganization;

		if (isSameQuery) return;

		lastQueryRef.current = { userId: tpoUserId, filtersKey, page: 1, organization: selectedOrganization };
		if (fetchData) {
			handleLoadData(1, apiFilters);
		}
		if (fetchCount) {
			fetchApiTotal();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiFilters, userDetails?.user_tpo_id, impersonatedUser?.user_tpo_id, selectedOrganization, fetchData, fetchCount]);

	return (
		<>
			<TableContainer
				component={Paper}
				sx={{
					maxHeight: isFullPage ? 'calc(100vh - 200px)' : maxHeight,
					minHeight: isFullPage ? 400 : minHeight,
					overflow: 'auto'
				}}
			>	<Stack spacing={1}>
					<Body2
						text={
						<span style={{ display: 'flex', alignItems: 'center', marginLeft: 15, marginTop:10 }}>
							<span style={{ color: 'red', fontWeight: 600, marginRight: 6 }}>
							Note:
							If a loan is not submitted within 10 days, it will be removed from Loan Upload History.</span>
						</span>
						}
					/>
				</Stack>

				<Table
					sx={{
						minWidth: isFullPage
							? {
									xs: 800,
									sm: 1000,
									md: showDirectPath ? 1300 : 1150,
									lg: showDirectPath ? 1460 : 1300,
							  }
							: showDirectPath
								? 1310
								: 1150,
						width: '100%'
					}}
					aria-label="upload history table"
					stickyHeader={isFullPage || fixedHeader}
				>
					<TableHeadUploadHistory
						data={uploadHistoryColumns}
						order={sortOrder}
						orderBy={orderBy}
						onRequestSort={handleRequestSort}
						filters={filters}
						onFiltersChange={handleFiltersChange}
					/>
					<TableBody>
						{filteredRows?.length === 0 ? (
							<TableRow>
								<TableCell colSpan={uploadHistoryColumns.length} align="center">
									No upload history found
								</TableCell>
							</TableRow>
						) : (
							filteredRows?.map((item, index) => (
								<TableRow
									key={getHistoryRowKey(item, index)}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									<TableCell align="left">{item?.borrower_name}</TableCell>
									<TableCell align="left">{item?.status}</TableCell>
									<TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
										{item?.loan_application_date
										? formatInTimeZone(new Date(item.loan_application_date), 'America/New_York', 'MM/dd/yyyy hh:mm a') + ' ET'
										: 'NA'}
									</TableCell>
									<TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
										{item?.loan_application_completed_date
										? formatInTimeZone(new Date(item.loan_application_completed_date), 'America/New_York', 'MM/dd/yyyy hh:mm a') + ' ET'
										: 'NA'}
									</TableCell>
									{showDirectPath && (
										<TableCell align="left">
											<DirectPathStatus
												selected={getDirectPathSelected(item, index)}
												locked={isDirectPathLocked(item)}
												onChange={(isDirectPath) =>
													handleDirectPathToggle(item, index, isDirectPath)
												}
											/>
										</TableCell>
									)}
									<TableCell align="left">
										<PrimaryButton
										text={item?.application_number || `${item?.borrower_name || ''} ${item?.address || ''}`.trim() || 'View'}
											onClick={() => handleUploadHistoryRowClick(item, index)}
											extraClass={
												item?.status?.toLowerCase().includes("created") ||
												item?.status?.toLowerCase().includes("completed") ||
												item?.status?.toLowerCase().includes("submitted")
													? `${styles.primaryBtnCustom} ${styles.completedLoan}`
													: styles.primaryBtnCustom
											}
										/>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination controls */}
		<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
				<Body3
				  text={`Page ${historyPage} of ${historyTotal && pageSize ? Math.max(1, Math.ceil(historyTotal / pageSize)) : 1}`}
				/>
				<Stack direction="row" gap={2}>
					<SecondaryButton
						text="Previous"
						onClick={async () => {
							if (historyPage > 1) {
									await handleLoadData(historyPage - 1, apiFilters);
							}
						}}
						disabled={historyPage <= 1 || uploadHistoryLoading}
					/>
					<PrimaryButton
						text="Next"
						onClick={async () => {
							const totalPages = historyTotal && pageSize ? Math.ceil(historyTotal / pageSize) : historyPage;
							if (historyPage < totalPages || historyHasNext) {
									await handleLoadData(historyPage + 1, apiFilters);
							}
						}}
						disabled={uploadHistoryLoading || (!historyHasNext && (historyTotal && pageSize ? historyPage >= Math.ceil(historyTotal / pageSize) : true))}
					/>
				</Stack>
			</Stack>

			{/* Processing Modal - only show when table is actually visible */}
			<ProcessingModal
				open={uploadHistoryLoading && showLoadingModal}
				text="Loading upload history..."
			/>
		</>
	);
};

export default UploadHistoryTable;